import { AuthContext, AuthContextType } from "@/features/app-layout/context";
import { axiosReq } from "@/lib/axios";
import { queryClient } from "@/lib/react-query" 
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { toast } from "sonner";

type UpdatePostBodyArgs = {
  post_id: number;
  post_content: string;
  hashtags: string[];
  post_date: string,
  post_time: string,
}

// API to update post body when manually edited
export const useUpdatePostBody = () => {

  const request = async (postBody: UpdatePostBodyArgs) => {
    const response = await axiosReq.post('/api/approval/post-save/', postBody)
    return response.data;
  }
  const { mutate: updatePostBody, isSuccess, isPending } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] })
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
      toast.success("Content updated")
    }
  })

  return { updatePostBody, isSuccess, isPending }
}


export const useRegenerateContent = () => {

  const { token } = useContext<AuthContextType>(AuthContext)


  const request = async ({ postId, signal }: { postId: number, signal: AbortSignal }): Promise<{
    message: string,
    post: {
      post_id: number;
      post_content: string;
      post_date: string;
      tags: string[],
      image_video_recommendation: string,
    },
  }> => {
    const response = await axiosReq.post('/api/approval/regenerate_posts/', {
      post_id: postId
    }, {
      signal, headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  }

  // API to regenerate content of a particular post
  const { mutate: regenerateContent, data: generatedData, status } = useMutation({
    mutationFn: (postId: number) => {
      const controller = new AbortController()
      return request({ postId, signal: controller.signal })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] });
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
      toast.success("Data generated")
    }
  })

  return { regenerateContent, generatedData, status }
}

// API to mark the post as scheduled
export const useSchedulePost = (location: "scheduled" | "review" | "discarded", onSchedule: (id: number) => void, eventIndex?: number) => {

  const { token } = useContext<AuthContextType>(AuthContext)


  const request = async (postId: number) => {
    const response = await axiosReq.post('/api/approval/schedule_post/', { post_id: postId }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  }

  const { mutate: schedulePost } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      toast.success("Event Scheduled successfully")
      if (location === "scheduled") {
        queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      } else if (location === "review") {
        queryClient.invalidateQueries({ queryKey: ["socials"] })
      } else {
        queryClient.invalidateQueries({ queryKey: ["discarded-socials"] })
      }
      if (eventIndex) {
        onSchedule(eventIndex)
      }
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] })

      queryClient.invalidateQueries({ queryKey: ["timeline"] })
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
    }
  })

  return { schedulePost }
}

export const useRegeneratePostImage = (setImageLoader: () => void) => {

  const request = async ({ id, catalog_id }: { id: number, catalog_id: number }): Promise<{
    status: string,
    data: {
      post_id: number;
      platform: string;
      text: string;
      image_video_recommendations: string;
      hashtags: string[];
      cta: string;
      date_of_posting: string;
      time_of_posting: string;
      image_url: string;
    }
  }> => {
    console.log(catalog_id)
    const response = await axiosReq.post('/api/approval/generate-images-post/', { post_id: id });
    if (response.data.data === null) {
      throw new Error("Error generating image")
    }
    return response.data
  }

  const { mutate: regenerateImage, data, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] })
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] })
      setImageLoader()
    },
    onError: () => {
      toast.message("Something went wrong")
    }
  })

  return { regenerateImage, status, data }

}

type imageUplaodArgs = {
  imageFile: File;
  postId: number;
  currentUrl: string;
}

export const useUploadPostImage = () => {

  const { token } = useContext<AuthContextType>(AuthContext)

  const request = async ({ imageFile, currentUrl, postId }: imageUplaodArgs): Promise<{
    post_id: number,
    image_path: string,
  }> => {
    const formData = {
      post_id: postId,
      signed_url: currentUrl,
      media_file: imageFile
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      },
    };

    const response = await axiosReq.post('/api/approval/upload-post-media/', formData, config);
    return response.data;
  };

  const { mutate: uploadImage, data, isSuccess, isPending } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] });
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] });
      toast.success("Image uploaded successfully");
      return data;
    },
    onError: (error) => {
      toast.error("Failed to upload image");
      console.error("Image upload error:", error);
    }
  });

  return { uploadImage, isSuccess, data, isPending };
};

export type Catalog = {
  image_url: string
  name: string,
  unique_identifier: string,
}

export const useGetCatalog = (open: boolean) => {

  const request = async (): Promise<{
    catalogue: Catalog[]
  }> => {
    const response = await axiosReq.get('/api/landing/catalogue/')
    return response.data
  }

  const { data: catalogData } = useQuery({
    queryKey: ["catalog"],
    queryFn: request,
    enabled: open
  })

  return { catalogData }
}

type AddFeedbackArgs = {
  post_id: number,
  rating: number | null,
  user_name: string,
  comment: string | null
}

export const useAddFeedback = () => {

  const request = async (data: AddFeedbackArgs) => {
    const response = await axiosReq.post('/api/landing/feedback/', data)
    return response.data
  }

  const { mutate: addFeedback } = useMutation({
    mutationFn: request
  })

  return { addFeedback }
}
